import React, {ReactElement} from 'react';
import {styled} from "@mui/material/styles";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import Grid from "@mui/material/Grid";
import * as Yup from "yup"
import {yupResolver} from "@hookform/resolvers/yup";
import Typography from "@mui/material/Typography";
import {AuthActions} from "./actions/auth";
import {IAuthState} from "./interface/auth";
import {useAppDispatch} from "../App/hooks/store";
import {AccountActions} from "../Account/actions/account";
import {TextField} from "../App/components/Input/TextField";
import {Button} from "../App/components/Button";
import {Link} from "../App/components/Link/Link";
import {Field} from "../App/components/Form/Field";

const Description = styled(Typography)(() => ({
  color: "rgb(148, 163, 184)"
}))

const Title = styled(Typography)(() => ({
  fontWeight: "500",
  fontSize: "1.3rem"
}))

const Logo = styled(Typography)(({theme}) => ({
  fontWeight: "600",
  fontSize: "1.5rem",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3)
}))

interface Props {
  setType: (option: 'login' | 'registration' | 'recovery') => void,
  onClose: () => void
}

const schema = Yup
  .object({
    email: Yup.string().email('Некорректный формат электронной почты').required("Введите адрес электронной почты"),
    password: Yup.string().min(8, "Длина пароля должна быть не менее 8 символов!").required("Введите пароль"),
    confirmation: Yup.string().required("Необходимо поддтвердить пароль").oneOf([Yup.ref("password")], "Пароли не совпадают"),
  }).required()

export function Registration(props: Props): ReactElement | null {
  const dispatch = useAppDispatch();
  const { setType, onClose } = props
  const { formState: { isSubmitSuccessful }, control, handleSubmit, reset, setError } = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmation: "",
    },
    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<IAuthState> = (data) => {
    dispatch(AuthActions.registration(data)).then(
      async () => {
        await dispatch(AccountActions.account())
        onClose()
      },
      error => {
        reset({
          email: data.email
        })
        setError('email', {
          type: 'manual',
          message: error
        })
      }
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
        <Grid item>
          <Grid container direction="row" justifyContent="center" alignItems="stretch" spacing={2}>
            <Grid item>
              <Logo>GAMEPARADE</Logo>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Title>Регистрация</Title>
        </Grid>
        <Field>
          <Controller
            name="email"
            control={control}
            render={({
              field: { onChange, value }, fieldState
            }) => (
              <TextField
                disabled={isSubmitSuccessful}
                required
                label="Электронная почта:"
                error={!!fieldState.error}
                onChange={onChange}
                value={value}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </Field>
        <Field>
          <Controller
            name="password"
            control={control}
            render={({
              field: { onChange, value }, fieldState
            }) => (
              <TextField
                disabled={isSubmitSuccessful}
                required
                type="password"
                label="Пароль:"
                error={!!fieldState.error}
                onChange={onChange}
                value={value}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </Field>
        <Field>
          <Controller
            name="confirmation"
            control={control}
            render={({
              field: { onChange, value }, fieldState
            }) => (
              <TextField
                disabled={isSubmitSuccessful}
                required
                type="password"
                label="Подтверждение пароля:"
                error={!!fieldState.error}
                onChange={onChange}
                value={value}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </Field>
        <Grid item>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <Grid item>
              <Button
                disabled={isSubmitSuccessful}
                size="large"
                type="submit"
              >
                Зарегистрироваться
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Grid item>
              <Grid container direction="row" justifyContent="center" alignItems="center" spacing={2}>
                <Grid item>
                  <Description>У вас уже есть аккаунт?</Description>
                </Grid>
                <Grid item>
                  <Link onClick={() => setType('login')}>
                    Войти
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}